import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import { Box, Grid, alpha, useTheme } from '@mui/material';
import * as echarts from 'echarts';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PercentIcon from '@mui/icons-material/Percent';

import { API_URL, httpClient } from '../../../httpClient';
import {
  CHART_COLORS,
  ChartCard,
  DashboardHeader,
  DateRangeFilter,
  EChartsComponent,
  type SourcingDashboardData,
  StatCard,
  toBrowserDayIsoRange,
  useDefaultDateRange,
} from './shared';

export const SourcingDashboard = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const notify = useNotify();
  const { ninetyDaysAgo, today } = useDefaultDateRange();

  const [startDate, setStartDate] = useState<string>(ninetyDaysAgo);
  const [endDate, setEndDate] = useState<string>(today);
  const [data, setData] = useState<SourcingDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Guard against a slow earlier response overwriting a newer one when the
    // date range changes rapidly (no AbortController → stale-response check).
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        const startIso = toBrowserDayIsoRange(startDate, false);
        const endIso = toBrowserDayIsoRange(endDate, true);
        if (startIso) queryParams.append('startDate', startIso);
        if (endIso) queryParams.append('endDate', endIso);

        const { json } = (await httpClient(
          `${API_URL}/job-application/dashboard/sourcing?${queryParams.toString()}`,
          { method: 'GET' },
        )) as { json: { data: SourcingDashboardData } };

        if (!cancelled) setData(json.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching sourcing dashboard:', error);
        notify('ra.notification.http_error', { type: 'error' });
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, notify]);

  const sourceOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.bySource ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      grid: { left: '3%', right: '4%', bottom: '3%', top: '8%', containLabel: true },
      xAxis: {
        type: 'category',
        data: points.map((p) => p.name),
        axisLabel: { rotate: 30, color: theme.palette.text.secondary, fontSize: 11 },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: theme.palette.text.secondary },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      },
      series: [
        {
          type: 'bar',
          data: points.map((p, i) => ({
            value: p.value,
            itemStyle: {
              color: CHART_COLORS[i % CHART_COLORS.length],
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '60%',
          label: { show: true, position: 'top', color: theme.palette.text.primary, fontSize: 11 },
        },
      ],
    };
  }, [data, theme]);

  const cityOption = useMemo<echarts.EChartsOption>(() => {
    const top10 = (data?.byCity ?? []).slice(0, 10);
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      legend: { bottom: '5%', left: 'center', textStyle: { color: theme.palette.text.secondary } },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: theme.palette.background.paper,
            borderWidth: 3,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{d}%',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#fff',
          },
          data: top10.map((p, i) => ({
            name: p.name,
            value: p.value,
            itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
          })),
        },
      ],
    };
  }, [data, theme]);

  const companyOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.byCompany ?? [];
    const reversed = [...points].reverse();
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { color: theme.palette.text.secondary },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: reversed.map((p) => p.name),
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
          width: 120,
          overflow: 'truncate',
          ellipsis: '...',
        },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      series: [
        {
          type: 'bar',
          data: reversed.map((p, i) => ({
            value: p.value,
            itemStyle: {
              color: CHART_COLORS[(points.length - 1 - i) % CHART_COLORS.length],
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '65%',
          label: {
            show: true,
            position: 'right',
            color: theme.palette.text.secondary,
            fontSize: 11,
            formatter: '{c}',
          },
        },
      ],
    };
  }, [data, theme]);

  const timelineOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.timeline ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      legend: {
        data: [
          translate('resources.job-application-dashboard.charts.daily'),
          translate('resources.job-application-dashboard.charts.cumulative'),
        ],
        top: '2%',
        textStyle: { color: theme.palette.text.secondary },
      },
      grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: points.map((p) => {
          const d = new Date(p.date);
          return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        }),
        axisLabel: { color: theme.palette.text.secondary, rotate: 45, fontSize: 10 },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      yAxis: [
        {
          type: 'value',
          name: translate('resources.job-application-dashboard.charts.daily'),
          position: 'left',
          axisLabel: { color: theme.palette.text.secondary },
          splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
        },
        {
          type: 'value',
          name: translate('resources.job-application-dashboard.charts.cumulative'),
          position: 'right',
          axisLabel: { color: theme.palette.text.secondary },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: translate('resources.job-application-dashboard.charts.daily'),
          type: 'bar',
          data: points.map((p) => p.count),
          itemStyle: { color: alpha('#6366f1', 0.8), borderRadius: [4, 4, 0, 0] },
        },
        {
          name: translate('resources.job-application-dashboard.charts.cumulative'),
          type: 'line',
          yAxisIndex: 1,
          data: points.map((p) => p.cumulative),
          smooth: true,
          lineStyle: { color: '#22c55e', width: 3 },
          itemStyle: { color: '#22c55e' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: alpha('#22c55e', 0.3) },
              { offset: 1, color: alpha('#22c55e', 0.05) },
            ]),
          },
        },
      ],
    };
  }, [data, theme, translate]);

  return (
    <Box sx={{ p: 3 }}>
      <Title title={translate('resources.job-application-dashboard.dashboards.tabSourcing')} />
      <DashboardHeader title={translate('resources.job-application-dashboard.dashboards.tabSourcing')}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </DashboardHeader>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.sourcing.total')}
            value={data?.total ?? 0}
            icon={PeopleIcon}
            color="#6366f1"
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.sourcing.contacted')}
            value={data?.contacted ?? 0}
            icon={CheckCircleIcon}
            color="#22c55e"
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.sourcing.contactRate')}
            value={data?.contactRate ?? '0%'}
            icon={PercentIcon}
            color="#06b6d4"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <ChartCard title={translate('resources.job-application-dashboard.dashboards.sourcing.timeline')}>
            <EChartsComponent option={timelineOption} height={350} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title={translate('resources.job-application-dashboard.dashboards.sourcing.bySource')}>
            <EChartsComponent option={sourceOption} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title={translate('resources.job-application-dashboard.dashboards.sourcing.byCity')}>
            <EChartsComponent option={cityOption} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ChartCard title={translate('resources.job-application-dashboard.dashboards.sourcing.byCompany')}>
            <EChartsComponent option={companyOption} loading={isLoading} />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};
